import { functions } from '../app/firebase.config';
import { httpsCallable } from "./firebaseCallable";

const GOOGLE_IDENTITY_SCRIPT = 'https://accounts.google.com/gsi/client';
const DRIVE_SCOPES = [
  'https://www.googleapis.com/auth/drive.file',
  'https://www.googleapis.com/auth/drive.readonly',
].join(' ');
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

let googleIdentityScriptPromise = null;

const loadGoogleIdentityScript = () => {
  if (window.google?.accounts?.oauth2) {
    return Promise.resolve();
  }

  if (!googleIdentityScriptPromise) {
    googleIdentityScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = GOOGLE_IDENTITY_SCRIPT;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Google Identity script.'));
      document.head.appendChild(script);
    });
  }

  return googleIdentityScriptPromise;
};

const cacheAccessToken = (accessToken, expiresAt) => {
  if (!accessToken) return;
  localStorage.setItem('google_access_token', accessToken);
  localStorage.setItem('google_access_token_timestamp', Date.now().toString());
  if (expiresAt) {
    localStorage.setItem('google_access_token_expires_at', String(expiresAt));
  }
};

/**
 * Set up the TeachLeague folder in user's Google Drive
 * Finds existing folder or creates a new one
 *
 * @param {string} accessToken - User's Google OAuth access token
 * @param {string} folderName - Optional custom folder name (default: "Teach League")
 * @returns {Promise<{folderId: string, folderName: string, folderLink: string, lessonPlansFolderId?: string, lessonPlansFolderName?: string, lessonPlansFolderLink?: string, existed: boolean}>}
 */
export async function setupDriveFolder(
  accessToken,
  folderName = 'Teach League',
  options = {}
) {
  try {
    const effectiveToken = await ensureDriveAccessToken(accessToken);

    console.log('📞 Calling Firebase function setupDriveFolder...');
    const setupFolder = httpsCallable(functions, 'setupDriveFolder');
    const result = await setupFolder({
      accessToken: effectiveToken,
      folderName,
      userFolderName: options.userFolderName,
      shareWithLink: options.shareWithLink !== false,
      shareRole: options.shareRole || 'reader',
    });

    console.log('✅ setupDriveFolder returned:', result.data);
    return result.data;
  } catch (error) {
    console.error('❌ Error setting up Drive folder:', error);
    console.error('❌ Error code:', error.code);
    console.error('❌ Error message:', error.message);
    console.error('❌ Error details:', error.details);

    // Handle authentication/authorization errors
    if (error.code === 'unauthenticated' || error.message?.includes('expired')) {
      clearExpiredToken();
      throw new Error('Session expired. Please sign in with Google again.');
    }

    if (error.code === 'permission-denied') {
      throw new Error('Insufficient permissions to access Google Drive. Please sign in again and grant Drive access.');
    }

    if (error.code === 'internal') {
      // Extract more details from the internal error
      const errorDetails = error.details || error.message || 'Unknown internal error';
      throw new Error(`Drive setup failed: ${errorDetails}`);
    }

    throw new Error(error.message || 'Failed to setup Drive folder');
  }
}

/**
 * Search user's Google Drive for educational resources
 *
 * @param {string} accessToken - User's Google OAuth access token
 * @param {string} query - Search query (e.g., lesson topic)
 * @param {Object} options - Search options
 * @param {string} options.folderId - Optional folder ID to restrict search
 * @param {Array<string>} options.fileTypes - Optional array of MIME types to search
 * @param {number} options.maxResults - Maximum number of results (default: 20)
 * @returns {Promise<{files: Array, query: string, timestamp: string}>}
 */
export async function searchDrive(accessToken, query, options = {}) {
  try {
    const effectiveToken = await ensureDriveAccessToken(accessToken);

    const searchDrive = httpsCallable(functions, 'searchGoogleDrive');
    const result = await searchDrive({
      accessToken: effectiveToken,
      query,
      folderId: options.folderId,
      fileTypes: options.fileTypes,
      maxResults: options.maxResults || 20,
    });

    return result.data;
  } catch (error) {
    console.error('Error searching Drive:', error);

    // Handle authentication/authorization errors
    if (error.code === 'unauthenticated' || error.message?.includes('expired')) {
      clearExpiredToken();
      throw new Error('Session expired. Please sign in with Google again.');
    }

    if (error.code === 'permission-denied') {
      throw new Error('Insufficient permissions to access Google Drive. Please sign in again and grant Drive access.');
    }

    throw new Error(error.message || 'Failed to search Drive');
  }
}

/**
 * List Drive folder contents (browse files and folders)
 * Supports TPT-style product folders that contain multiple files
 *
 * @param {string} accessToken - User's Google OAuth access token
 * @param {Object} options - Browse options
 * @param {string} options.folderId - Optional folder ID to browse (defaults to parent Teach League folder)
 * @param {number} options.maxResults - Maximum number of results (default: 100)
 * @param {boolean} options.recursive - List all nested contents (default: false)
 * @returns {Promise<{contents: Array, folders: Array, files: Array, totalCount: number}>}
 */
export async function listDriveContents(accessToken, options = {}) {
  try {
    const effectiveToken = await ensureDriveAccessToken(accessToken);

    const listContents = httpsCallable(functions, 'listDriveContents');
    const result = await listContents({
      accessToken: effectiveToken,
      folderId: options.folderId,
      maxResults: options.maxResults || 100,
      recursive: options.recursive || false,
    });

    return result.data;
  } catch (error) {
    console.error('Error listing Drive contents:', error);

    // Handle authentication/authorization errors
    if (error.code === 'unauthenticated' || error.message?.includes('expired')) {
      clearExpiredToken();
      throw new Error('Session expired. Please sign in with Google again.');
    }

    if (error.code === 'permission-denied') {
      throw new Error('Insufficient permissions to access Google Drive. Please sign in again and grant Drive access.');
    }

    throw new Error(error.message || 'Failed to list Drive contents');
  }
}

/**
 * Check if access token is expired (older than 1 hour)
 *
 * @param {string|null} tokenTimestamp - Timestamp when token was stored (from localStorage)
 * @returns {boolean} True if token is expired or timestamp is missing
 */
export function isTokenExpired(tokenTimestamp) {
  if (!tokenTimestamp) return true;

  const tokenAge = Date.now() - parseInt(tokenTimestamp);
  const ONE_HOUR = 3600000; // 1 hour in milliseconds

  return tokenAge > ONE_HOUR;
}

/**
 * Clear expired token from localStorage
 * Call this when token validation fails or when token is known to be expired
 */
export function clearExpiredToken() {
  localStorage.removeItem('google_access_token');
  localStorage.removeItem('google_access_token_timestamp');
  localStorage.removeItem('google_access_token_expires_at');
  console.log('🗑️ Cleared expired Google access token');
}

/**
 * Helper to check if user has Drive access enabled
 *
 * @param {Object} currentUser - Redux user object
 * @returns {boolean}
 */
export function hasDriveAccess(currentUser) {
  if (currentUser?.driveEnabled) return true;
  return localStorage.getItem('google_drive_connected') === 'true';
}

/**
 * Helper to get Drive access token from current user
 *
 * @param {Object} currentUser - Redux user object
 * @returns {string|null}
 */
export function getDriveAccessToken(currentUser) {
  return currentUser?.googleAccessToken || localStorage.getItem('google_access_token');
}

export async function requestDriveAuthorizationCode({prompt = 'consent select_account'} = {}) {
  if (!GOOGLE_CLIENT_ID) {
    throw new Error('Missing REACT_APP_GOOGLE_CLIENT_ID. Set it in .env and restart the app.');
  }

  console.log('🔐 Using Google OAuth client ID (suffix):', GOOGLE_CLIENT_ID.slice(-8));

  await loadGoogleIdentityScript();

  return new Promise((resolve, reject) => {
    const client = window.google.accounts.oauth2.initCodeClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: DRIVE_SCOPES,
      prompt,
      ux_mode: 'popup',
      callback: (response) => {
        if (response?.code) {
          resolve(response.code);
          return;
        }
        reject(new Error(response?.error || 'Failed to get Google authorization code.'));
      },
    });
    client.requestCode();
  });
}

export async function exchangeGoogleDriveCode(code) {
  const exchange = httpsCallable(functions, 'exchangeGoogleDriveCode');
  const result = await exchange({ code });
  localStorage.setItem('google_drive_connected', 'true');
  return result.data;
}

export async function getGoogleDriveToken() {
  const getToken = httpsCallable(functions, 'getGoogleDriveToken');
  const result = await getToken();
  const { accessToken, expiresAt } = result.data || {};
  if (accessToken) {
    cacheAccessToken(accessToken, expiresAt);
  }
  return result.data;
}

export async function getGoogleDriveStatus() {
  const getStatus = httpsCallable(functions, 'getGoogleDriveStatus');
  const result = await getStatus();
  return result.data;
}

export async function disconnectGoogleDrive() {
  const disconnect = httpsCallable(functions, 'disconnectGoogleDrive');
  const result = await disconnect();
  localStorage.removeItem('google_drive_connected');
  clearExpiredToken();
  return result.data;
}

export async function ensureDriveAccessToken(accessToken) {
  const cached = accessToken || localStorage.getItem('google_access_token');
  const expiresAtRaw = localStorage.getItem('google_access_token_expires_at');
  const tokenTimestamp = localStorage.getItem('google_access_token_timestamp');

  if (cached) {
    if (expiresAtRaw) {
      const expiresAt = parseInt(expiresAtRaw, 10);
      if (Date.now() < expiresAt - 60000) {
        return cached;
      }
    } else if (!isTokenExpired(tokenTimestamp)) {
      return cached;
    }
  }

  try {
    const tokenResult = await getGoogleDriveToken();
    return tokenResult?.accessToken || null;
  } catch (error) {
    const message = error?.message || '';
    const isReconnectRequired =
      error?.code === 'unauthenticated' ||
      error?.code === 'not-found' ||
      /reconnect google drive|access was revoked|session expired|not connected/i.test(message);

    console.warn('Failed to refresh Drive token from server:', message || error);

    if (isReconnectRequired) {
      clearExpiredToken();
      localStorage.removeItem('google_drive_connected');
      return null;
    }

    return accessToken || null;
  }
}

/**
 * Fetch unique contributors from the shared Teach League folder
 * Returns list of users who have contributed materials
 *
 * @param {string} accessToken - User's Google OAuth access token
 * @param {string} parentFolderId - Parent Teach League folder ID
 * @returns {Promise<Array<{name: string, email: string, photoUrl: string, folderId: string, folderLink: string}>>}
 */
export async function fetchSharedContributors(accessToken, parentFolderId) {
  try {
    const effectiveToken = await ensureDriveAccessToken(accessToken);

    // List contents of parent folder to get user subfolders
    const contents = await listDriveContents(effectiveToken, {
      folderId: parentFolderId,
      maxResults: 50,
    });

    // Extract unique contributors from folders (each user has their own subfolder)
    const contributors = [];
    const seenKeys = new Set();

    if (contents?.folders) {
      for (const folder of contents.folders) {
        // Each subfolder represents a contributor
        const legacyOwner = folder.owners?.[0] || null;
        const owner = folder.owner || null;
        const contributor = folder.contributor || null;

        const folderName = contributor?.folderName || folder.name || 'Unknown';
        const folderNameLower = folderName.toLowerCase();

        let name =
          contributor?.name ||
          owner?.name ||
          legacyOwner?.displayName ||
          folderName ||
          'Unknown';

        let email =
          contributor?.email ||
          owner?.email ||
          legacyOwner?.emailAddress ||
          (folderName && folderName.includes('@') ? folderName : null);

        const emailLower = (email || '').toLowerCase();
        const nameLower = (name || '').toLowerCase();
        let isServiceAccount =
          emailLower.endsWith('gserviceaccount.com') ||
          emailLower.includes('teachleague-drive-manager') ||
          nameLower.includes('teachleague-drive-manager') ||
          (nameLower.includes('teachleague') && nameLower.includes('drive manager'));

        const isSystemFolder =
          folderNameLower === 'premium' ||
          folderNameLower === 'teach league' ||
          folderNameLower.includes('drive manager') ||
          folderNameLower.includes('service account');

        if (isServiceAccount && !isSystemFolder && folderName) {
          name = folderName;
          email = folderName.includes('@') ? folderName : null;
          isServiceAccount = false;
        }

        if (isServiceAccount || isSystemFolder) {
          continue;
        }

        const key = email || folder.id || name;
        if (!seenKeys.has(key)) {
          seenKeys.add(key);
          contributors.push({
            name,
            email,
            photoUrl: contributor?.photoUrl || legacyOwner?.photoLink || null,
            folderId: folder.id,
            folderLink: folder.link || folder.webViewLink,
            folderName: contributor?.folderName || folder.name,
          });
        }
      }
    }

    return contributors;
  } catch (error) {
    console.error('Error fetching contributors:', error);
    return [];
  }
}

/**
 * Format Drive files for display in UI
 *
 * @param {Array} files - Array of Drive file objects
 * @returns {Array} Formatted file objects
 */
export function formatDriveFiles(files) {
  return files.map(file => ({
    ...file,
    // Get readable file type
    fileType: getReadableFileType(file.mimeType),
    // Format modified time
    modifiedDate: new Date(file.modifiedTime).toLocaleDateString(),
    // Shorten name if too long
    displayName: file.name.length > 50 ? file.name.substring(0, 47) + '...' : file.name,
    // Contributor info (from Cloud Function)
    contributorName: file.contributorName,
    contributorEmail: file.contributorEmail,
    contributorFolderName: file.contributorFolderName,
  }));
}

/**
 * Get human-readable file type from MIME type
 *
 * @param {string} mimeType
 * @returns {string}
 */
function getReadableFileType(mimeType) {
  const typeMap = {
    'application/pdf': 'PDF',
    'application/vnd.google-apps.document': 'Google Doc',
    'application/vnd.google-apps.presentation': 'Google Slides',
    'application/vnd.google-apps.spreadsheet': 'Google Sheets',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word Doc',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint',
  };

  return typeMap[mimeType] || 'Document';
}
